import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Badge } from '../components/ui/badge';
import api from '../api/axios';
import { toast } from 'sonner';
import { Plus, Calendar, Sparkles, ExternalLink, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { tr, enUS, de, es, fr } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export const Changelog = () => {
  const { activeProject } = useAuth();
  const { language, t } = useLanguage();
  
  // Tarih formatı için locale seçimi
  const dateLocale = {
    tr: tr,
    en: enUS,
    de: de,
    es: es,
    fr: fr
  }[language] || enUS;
  const [changelogs, setChangelogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [changelogToDelete, setChangelogToDelete] = useState(null);
  const [newChangelog, setNewChangelog] = useState({
    version: '',
    title: '',
    content: ''
  });

  useEffect(() => {
    if (activeProject) {
      fetchChangelogs();
    }
  }, [activeProject]);

  const fetchChangelogs = async () => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const response = await api.get(`/changelog?project_id=${activeProject.id}`);
      setChangelogs(response.data);
    } catch (error) {
      console.error('Failed to fetch changelogs:', error);
      toast.error(t.changelogLoadFailed);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!activeProject) {
      toast.error(t.pleaseSelectProject);
      return;
    }
    try {
      await api.post(`/changelog?project_id=${activeProject.id}`, newChangelog);
      toast.success(t.changelogCreated);
      setShowDialog(false);
      setNewChangelog({ version: '', title: '', content: '' });
      fetchChangelogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.changelogCreateFailed);
    }
  };

  const handleDeleteClick = (changelog) => {
    setChangelogToDelete(changelog);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!changelogToDelete) return;
    
    try {
      await api.delete(`/changelog/${changelogToDelete.id}`);
      toast.success(t.changelogDeleted);
      setShowDeleteDialog(false);
      setChangelogToDelete(null);
      fetchChangelogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || t.changelogDeleteFailed);
      setShowDeleteDialog(false);
      setChangelogToDelete(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Product Changelog - FeedPanels</title>
        <meta name="description" content="Create and publish product changelogs in FeedPanels. Share releases, new features, and bug fixes with your customers." />
        <link rel="canonical" href="https://feedpanels.com/dashboard/changelog" />
      </Helmet>
      <>
      <div className="p-4 md:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-4 md:gap-0 mb-6 md:mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2" data-testid="changelog-page-title">
                {t.changelogTitle}
              </h1>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">{t.changelogDescription}</p>
              {activeProject && (
                <a
                  href={`/changelog/${activeProject.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-xs md:text-sm text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300 mt-2"
                  data-testid="public-changelog-link"
                >
                  <ExternalLink className="w-3 md:w-4 h-3 md:h-4 mr-1" />
                  {t.viewPublicChangelog}
                </a>
              )}
            </div>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button className="w-full md:w-auto text-xs md:text-sm" data-testid="new-changelog-button">
                  <Plus className="w-3 md:w-4 h-3 md:h-4 mr-2" />
                  {t.newRelease}
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-lg md:text-xl dark:text-white">{t.createChangelogEntry}</DialogTitle>
                  <DialogDescription className="text-xs md:text-sm dark:text-gray-400">
                    {t.createChangelogDescription}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-3 md:space-y-4">
                  <div>
                    <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.version}</label>
                    <Input
                      value={newChangelog.version}
                      onChange={(e) => setNewChangelog({ ...newChangelog, version: e.target.value })}
                      placeholder={t.versionPlaceholder}
                      required
                      data-testid="changelog-version-input"
                      className="text-xs md:text-sm mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.title}</label>
                    <Input
                      value={newChangelog.title}
                      onChange={(e) => setNewChangelog({ ...newChangelog, title: e.target.value })}
                      placeholder={t.titlePlaceholder}
                      required
                      data-testid="changelog-title-input"
                      className="text-xs md:text-sm mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs md:text-sm font-medium dark:text-gray-300">{t.content}</label>
                    <Textarea
                      value={newChangelog.content}
                      onChange={(e) => setNewChangelog({ ...newChangelog, content: e.target.value })}
                      placeholder={t.contentPlaceholder}
                      required
                      rows={8}
                      data-testid="changelog-content-input"
                      className="text-xs md:text-sm mt-1"
                    />
                  </div>
                  <Button type="submit" className="w-full text-xs md:text-sm" data-testid="changelog-submit-button">
                    {t.publishChangelog}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="text-center py-12 text-gray-600 dark:text-gray-400">{t.loadingChangelog}</div>
          ) : changelogs.length === 0 ? (
            <Card className="p-12 text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 mb-2">{t.noChangelogEntries}</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">{t.createFirstReleaseNote}</p>
            </Card>
          ) : (
            <div className="space-y-4 md:space-y-6">
              {changelogs.map((entry) => (
                <Card key={entry.id} data-testid={`changelog-entry-${entry.id}`}>
                  <CardContent className="p-3 md:p-6">
                    <div className="flex flex-col md:flex-row items-start md:items-center md:justify-between gap-3 md:gap-0 mb-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 md:space-x-3 mb-2 flex-wrap gap-2 md:gap-0">
                          <Badge className="bg-cyan-600 dark:bg-cyan-700 text-white dark:text-cyan-100 text-xs md:text-sm" data-testid={`version-badge-${entry.id}`}>
                            {entry.version}
                          </Badge>
                          <h2 className="text-base md:text-xl font-bold text-gray-900 dark:text-white break-words">{entry.title}</h2>
                        </div>
                        <div className="flex items-center text-xs md:text-sm text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 md:w-4 h-3 md:h-4 mr-1 flex-shrink-0" />
                          {format(new Date(entry.release_date), 'MMMM d, yyyy', { locale: dateLocale })}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(entry)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0 flex-shrink-0"
                        data-testid={`delete-changelog-${entry.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <pre className="whitespace-pre-wrap text-xs md:text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 p-3 md:p-4 rounded-lg font-sans overflow-x-auto">
                        {entry.content}
                      </pre>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="dark:text-white">{t.deleteChangelogEntry}</AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-400">
              {t.deleteChangelogConfirm} <strong>"{changelogToDelete?.title}"</strong> ({t.version} {changelogToDelete?.version})?
              {t.deleteChangelogWarning}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setChangelogToDelete(null)}>{t.cancel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {t.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
    </>
  );
};